/*
 * ScreenRecorder — captures the user's screen (via getDisplayMedia) plus an
 * optional microphone track, records to a WebM Blob using MediaRecorder, and
 * exposes the resulting Blob + duration back to the parent via onRecorded().
 *
 * MVP: single recording per session. To re-record, click "Discard".
 */
import { useEffect, useRef, useState } from "react";
import { Video, Mic, MicOff, Square, RefreshCw, AlertTriangle } from "lucide-react";

const SUPPORTED = typeof navigator !== "undefined"
  && navigator.mediaDevices
  && typeof navigator.mediaDevices.getDisplayMedia === "function";

// Pick a codec the current browser will accept. Chrome/Edge → vp9, Firefox → vp8,
// Safari can be quirky (may not support screen capture at all).
function preferredMimeType() {
  if (typeof MediaRecorder === "undefined") return "";
  const candidates = [
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm;codecs=h264,opus",
    "video/webm",
    "video/mp4",
  ];
  for (const c of candidates) {
    if (MediaRecorder.isTypeSupported(c)) return c;
  }
  return "";
}

function formatTime(s) {
  s = Math.max(0, Math.floor(s));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2,"0")}:${String(r).padStart(2,"0")}`;
}

export default function ScreenRecorder({ onRecorded, blob }) {
  const [micEnabled, setMicEnabled] = useState(true);
  const [recording, setRecording] = useState(false);
  const [error, setError] = useState("");
  const [elapsed, setElapsed] = useState(0);
  const [previewUrl, setPreviewUrl] = useState(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const startedAtRef = useRef(0);
  const livePreviewRef = useRef(null);

  useEffect(() => {
    // If parent already has a blob (e.g. wizard step re-mount) show its preview.
    if (blob && !previewUrl) setPreviewUrl(URL.createObjectURL(blob));
    return () => { if (previewUrl) URL.revokeObjectURL(previewUrl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blob]);

  useEffect(() => () => stopStreamsSafely(), []);

  function stopStreamsSafely() {
    try {
      if (recorderRef.current && recorderRef.current.state !== "inactive") {
        recorderRef.current.stop();
      }
    } catch (err) {
      if (process.env.NODE_ENV === "development") console.warn("stop recorder failed", err);
    }
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
      }
    } catch (err) {
      if (process.env.NODE_ENV === "development") console.warn("stop tracks failed", err);
    }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    streamRef.current = null;
    recorderRef.current = null;
  }

  async function startRecording() {
    setError("");
    setElapsed(0);
    chunksRef.current = [];
    if (!SUPPORTED) {
      setError("Your browser doesn't support screen recording. Try Chrome, Edge, or Firefox on desktop.");
      return;
    }
    let display, mic;
    try {
      display = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: { ideal: 30, max: 60 } },
        audio: true, // tab/system audio (Chrome-only, best-effort)
      });
    } catch (e) {
      setError(e?.message?.includes("Permission") ? "Screen sharing permission was denied." : "Could not start screen capture.");
      return;
    }
    if (micEnabled) {
      try {
        mic = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      } catch (e) {
        // Fall through — recording continues without mic
        if (process.env.NODE_ENV === "development") console.warn("mic denied", e);
      }
    }

    // Merge display + mic audio tracks into one stream.
    const combined = new MediaStream();
    display.getVideoTracks().forEach(t => combined.addTrack(t));
    // Merge audio tracks into a single track via Web Audio API so the
    // MediaRecorder actually captures both sources simultaneously.
    const audioTracks = [];
    display.getAudioTracks().forEach(t => audioTracks.push(t));
    if (mic) mic.getAudioTracks().forEach(t => audioTracks.push(t));
    if (audioTracks.length > 0) {
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const dest = ctx.createMediaStreamDestination();
        audioTracks.forEach(t => {
          const s = new MediaStream([t]);
          const src = ctx.createMediaStreamSource(s);
          src.connect(dest);
        });
        dest.stream.getAudioTracks().forEach(t => combined.addTrack(t));
      } catch (e) {
        // Fallback: just add whichever audio track we have
        audioTracks.forEach(t => combined.addTrack(t));
      }
    }

    // Auto-stop when the user clicks the browser's "Stop sharing" chip
    display.getVideoTracks()[0].addEventListener("ended", () => {
      stopRecording();
    });

    streamRef.current = combined;
    if (livePreviewRef.current) {
      livePreviewRef.current.srcObject = combined;
    }

    const mimeType = preferredMimeType();
    let rec;
    try {
      rec = mimeType ? new MediaRecorder(combined, { mimeType, videoBitsPerSecond: 3_000_000 })
                     : new MediaRecorder(combined);
    } catch (e) {
      setError("MediaRecorder couldn't start. Update your browser and try again.");
      stopStreamsSafely();
      return;
    }
    rec.ondataavailable = (evt) => { if (evt.data && evt.data.size > 0) chunksRef.current.push(evt.data); };
    rec.onstop = () => {
      const type = (rec.mimeType || "").split(";")[0] || "video/webm";
      const finalBlob = new Blob(chunksRef.current, { type });
      const durSec = Math.round((Date.now() - startedAtRef.current) / 1000);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      const url = URL.createObjectURL(finalBlob);
      setPreviewUrl(url);
      setRecording(false);
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      onRecorded?.(finalBlob, durSec, type);
    };
    recorderRef.current = rec;
    startedAtRef.current = Date.now();
    setRecording(true);
    rec.start(1000); // gather chunks every 1s
    timerRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startedAtRef.current) / 1000));
    }, 500);
  }

  function stopRecording() {
    try {
      if (recorderRef.current && recorderRef.current.state !== "inactive") {
        recorderRef.current.stop();
      }
    } catch (err) {
      if (process.env.NODE_ENV === "development") console.warn("stopRecording (recorder) failed", err);
    }
    try {
      streamRef.current?.getTracks().forEach(t => t.stop());
    } catch (err) {
      if (process.env.NODE_ENV === "development") console.warn("stopRecording (tracks) failed", err);
    }
  }

  function discard() {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setElapsed(0);
    onRecorded?.(null, 0, null);
  }

  if (!SUPPORTED) {
    return (
      <div className="brutal-border p-4 bg-state-danger-surface flex items-start gap-2" data-testid="screen-recorder-unsupported">
        <AlertTriangle size={16} className="mt-0.5 text-ink-accent"/>
        <div className="text-xs">
          <div className="font-black uppercase tracking-widest text-ink">Screen recording not supported</div>
          <div className="text-ink-muted mt-1">Your browser doesn't expose <code>getDisplayMedia</code>. Please use the latest Chrome, Edge, or Firefox on desktop.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="brutal-border p-5 space-y-4" data-testid="screen-recorder">
      <div className="flex items-center justify-between">
        <div>
          <div className="marquee-tag mb-1">// SCREEN RECORDING</div>
          <div className="text-xs text-ink-muted uppercase tracking-widest">
            Record your app + optional mic narration. Chunk in ≤10 minutes for best results.
          </div>
        </div>
        <label className="flex items-center gap-2 text-xs uppercase tracking-widest cursor-pointer select-none">
          <input
            type="checkbox"
            checked={micEnabled}
            onChange={(e)=>setMicEnabled(e.target.checked)}
            disabled={recording}
            className="align-middle"
            data-testid="screen-mic-toggle"
          />
          {micEnabled ? <Mic size={14}/> : <MicOff size={14}/>}
          <span>Mic</span>
        </label>
      </div>

      {error && (
        <div className="brutal-border p-3 bg-state-danger-surface flex items-start gap-2 text-xs" data-testid="screen-recorder-error">
          <AlertTriangle size={14} className="mt-0.5 text-ink-accent"/>
          <span>{error}</span>
        </div>
      )}

      {!previewUrl && !recording && (
        <button
          onClick={startRecording}
          className="brutal-btn w-full justify-center"
          data-testid="btn-start-screen-recording"
        >
          <Video size={16}/> Start recording
        </button>
      )}

      {recording && (
        <div className="space-y-3" data-testid="screen-recording-live">
          <video
            ref={livePreviewRef}
            autoPlay
            muted
            playsInline
            className="w-full brutal-border bg-black aspect-video"
          />
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 font-mono-ui text-sm">
              <span className="inline-block w-3 h-3 rounded-full bg-brand animate-pulse"/>
              <span data-testid="screen-recording-elapsed">REC · {formatTime(elapsed)}</span>
            </div>
            <button
              onClick={stopRecording}
              className="brutal-btn"
              data-testid="btn-stop-screen-recording"
            >
              <Square size={14}/> Stop
            </button>
          </div>
        </div>
      )}

      {previewUrl && !recording && (
        <div className="space-y-3" data-testid="screen-recording-preview">
          <video
            src={previewUrl}
            controls
            className="w-full brutal-border bg-black aspect-video"
            data-testid="screen-recording-preview-video"
          />
          <div className="flex justify-between items-center text-xs uppercase tracking-widest">
            <span>Duration ≈ {formatTime(elapsed)}</span>
            <div className="flex gap-2">
              <button
                onClick={()=>{ discard(); startRecording(); }}
                className="brutal-btn brutal-btn-secondary"
                data-testid="btn-rerecord-screen"
              >
                <RefreshCw size={14}/> Re-record
              </button>
              <button
                onClick={discard}
                className="brutal-btn brutal-btn-ghost"
                data-testid="btn-discard-screen"
              >
                Discard
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
